import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRightOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';

import PageTitleBar from '@iso/components/utility/PageTitleBar';
import api from '@iso/services/api';
import apiBoard from '@iso/services/repository/board';
import { b64_to_utf8, formatMoney } from '@iso/lib/helpers/utility';

import ForceGraph from './engine/ForceGraph';
import { PRODUTOS_NETWORK, PRODUTOS_TYPE_LABELS } from './engine/networkConfig';
import {
  Shell,
  Title,
  Dock,
  Search,
  Rail,
  Card,
  CardTitle,
  Levels,
  LevelBtn,
  FilterRow,
  Panel,
  PanelHead,
  Meta,
  Action,
} from './engine/NetworkUI';

const withTimeout = (promise, ms = 20000) =>
  Promise.race([
    promise,
    new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);

const readSession = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    return String(b64_to_utf8(raw) || '').split(',').filter(Boolean);
  } catch (error) {
    return [];
  }
};

const idOf = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value._id || value.id || null;
  return value;
};

const money = (value) => {
  try {
    return formatMoney(Number(value) || 0);
  } catch (error) {
    return String(value || '');
  }
};

export default function ConfluenciaProdutos() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [relationsLoading, setRelationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [people, setPeople] = useState(new Map());
  const [activities, setActivities] = useState([]);
  const [sales, setSales] = useState([]);
  const [bills, setBills] = useState([]);
  const [level, setLevel] = useState(3);
  const [hidden, setHidden] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const permissions = readSession('permissions');
      const isAdmin = permissions.includes('all');
      const boardsAccess = readSession('boards_access');
      try {
        const [prodRes, peopleRes, infoRes, salesRes, billsRes] = await Promise.all([
          withTimeout(api.get('/productsAndServices/all')).catch(() => null),
          withTimeout(api.get('/people/findSmallAll')).catch(() => null),
          withTimeout(apiBoard.getAllInfo()).catch(() => null),
          withTimeout(api.get('/sales')).catch(() => null),
          withTimeout(api.get('/bills')).catch(() => null),
        ]);
        const peopleMap = new Map();
        const peopleList = peopleRes && Array.isArray(peopleRes.data) ? peopleRes.data : [];
        peopleList.forEach((person) => {
          if (person && person._id) peopleMap.set(String(person._id), person);
        });
        const rawBoards = infoRes && Array.isArray(infoRes.data) ? infoRes.data : [];
        const allowed = isAdmin
          ? rawBoards
          : rawBoards.filter((board) => boardsAccess.includes(String(board._id)));
        const confirmedSales = (salesRes && Array.isArray(salesRes.data) ? salesRes.data : []).filter(
          (sale) => sale && sale.status === 'concluida',
        );
        const billList = billsRes && Array.isArray(billsRes.data) ? billsRes.data : [];

        if (!alive) return;
        setProducts(prodRes && Array.isArray(prodRes.data) ? prodRes.data : []);
        setPeople(peopleMap);
        setSales(confirmedSales);
        setBills(billList);
        setLoading(false);

        const acts = await Promise.all(
          allowed.map((board) =>
            withTimeout(apiBoard.getBoardData(board._id, 'activities'))
              .then((res) => (Array.isArray(res.data) ? res.data : []))
              .catch(() => []),
          ),
        );
        if (!alive) return;
        setActivities(
          acts.flatMap((list, index) =>
            list.map((activity) => ({ ...activity, __boardId: allowed[index]._id })),
          ),
        );
      } catch (err) {
        console.error('[produtos] falha ao carregar', err);
        if (alive) setError('Não foi possível carregar os produtos agora.');
      } finally {
        if (alive) {
          setLoading(false);
          setRelationsLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const activityByProduct = useMemo(() => {
    const map = new Map();
    activities.forEach((activity) => {
      (activity.productsAndServices || []).forEach((item) => {
        const id = idOf(item.item);
        if (!id) return;
        if (!map.has(String(id))) map.set(String(id), []);
        map.get(String(id)).push(activity);
      });
    });
    return map;
  }, [activities]);

  const salesByProduct = useMemo(() => {
    const map = new Map();
    sales.forEach((sale) => {
      (sale.itens || []).forEach((item) => {
        const id = idOf(item.productId);
        if (!id) return;
        if (!map.has(String(id))) map.set(String(id), []);
        map.get(String(id)).push(sale);
      });
    });
    return map;
  }, [sales]);

  const activeProducts = useMemo(() => {
    if (relationsLoading) return products;
    return products.filter(
      (product) =>
        (Array.isArray(product.suppliers) && product.suppliers.length > 0) ||
        activityByProduct.has(String(product._id)) ||
        salesByProduct.has(String(product._id)),
    );
  }, [products, activityByProduct, salesByProduct, relationsLoading]);

  const counts = useMemo(() => {
    const suppliers = new Set();
    activeProducts.forEach((product) => {
      (product.suppliers || []).forEach((supplier) => {
        const id = idOf(supplier);
        if (id) suppliers.add(String(id));
      });
    });
    return {
      produto: activeProducts.length,
      venda: sales.length,
      fornecedor: suppliers.size,
      cliente: new Set(sales.map((sale) => String(idOf(sale.client))).filter(Boolean)).size,
      atividade: activities.length,
      financeiro: bills.length,
    };
  }, [activeProducts, sales, activities, bills]);

  const graph = useMemo(() => {
    const nodes = [];
    const links = [];
    const ids = new Set();
    const addNode = (node) => {
      if (ids.has(node.id)) return;
      ids.add(node.id);
      nodes.push(node);
    };
    const addLink = (source, target, options) => {
      if (!ids.has(source) || !ids.has(target)) return;
      links.push({ source, target, ...options });
    };

    const shownProducts = activeProducts.slice(0, PRODUTOS_NETWORK.caps.products);
    if (!hidden.has('produto')) {
      shownProducts.forEach((product) => {
        addNode({
          id: 'prd:' + product._id,
          label: product.name || product.alias || 'Produto',
          type: 'produto',
          color: '#5fb3a1',
          size: 11,
          data: product,
        });
      });
    }
    const productIds = new Set(shownProducts.map((product) => 'prd:' + product._id));

    // Fornecedores do cadastro
    if (level >= 2 && !hidden.has('fornecedor')) {
      shownProducts.forEach((product) => {
        (product.suppliers || []).forEach((supplier) => {
          const supplierId = idOf(supplier);
          if (!supplierId) return;
          const person = people.get(String(supplierId));
          addNode({
            id: 'sup:' + supplierId,
            label: (person && (person.name || person.fantasyName)) || 'Fornecedor',
            type: 'fornecedor',
            color: '#63c08d',
            size: 8,
            data: person || { _id: supplierId },
          });
          addLink('prd:' + product._id, 'sup:' + supplierId, {
            color: '#63c08d',
            len: 80,
            spring: 0.03,
          });
        });
      });
    }

    // Vendas confirmadas contendo o produto
    const salesShown = level >= 2
      ? [...new Set(shownProducts.flatMap((product) => salesByProduct.get(String(product._id)) || []))]
      : [];
    if (level >= 2 && !hidden.has('venda')) {
      salesShown.slice(0, 200).forEach((sale) => {
        addNode({
          id: 'sale:' + sale._id,
          label: 'Venda ' + String(sale._id).slice(-6),
          type: 'venda',
          color: '#f2c14e',
          size: 8,
          data: sale,
        });
      });
    }
    if (level >= 2) {
      shownProducts.forEach((product) => {
        (salesByProduct.get(String(product._id)) || []).slice(0, 20).forEach((sale) => {
          addLink('prd:' + product._id, 'sale:' + sale._id, {
            color: '#f2c14e',
            len: 80,
            spring: 0.03,
            kind: 'encadeamento',
            arrow: true,
          });
        });
      });
      // clientes das vendas
      if (!hidden.has('cliente')) {
        salesShown.slice(0, 200).forEach((sale) => {
          const clientId = idOf(sale.client);
          if (!clientId) return;
          const person = people.get(String(clientId));
          addNode({
            id: 'cli:' + clientId,
            label: (person && (person.name || person.fantasyName)) || 'Cliente',
            type: 'cliente',
            color: '#b08394',
            size: 7,
            data: person || { _id: clientId },
          });
          addLink('sale:' + sale._id, 'cli:' + clientId, { color: '#b08394', len: 70, spring: 0.03 });
        });
      }
    }

    // Atividades: usam o produto e/ou são a atividade da venda
    const activitiesById = new Map(activities.map((activity) => [String(activity._id), activity]));
    const actsShown = level >= 3
      ? [
          ...new Set([
            ...shownProducts.flatMap((product) => activityByProduct.get(String(product._id)) || []),
            ...salesShown
              .map((sale) => (sale.activityId ? activitiesById.get(String(sale.activityId)) : null))
              .filter(Boolean),
          ]),
        ]
      : [];
    if (level >= 3 && !hidden.has('atividade')) {
      actsShown.slice(0, 300).forEach((activity) => {
        addNode({
          id: 'act:' + activity._id,
          label: activity.title || (activity.controlNumber ? '#' + activity.controlNumber : '') || 'Atividade',
          type: 'atividade',
          color: '#e2e8f0',
          size: 7.5,
          boardId: activity.__boardId,
          data: activity,
        });
      });
    }
    if (level >= 3) {
      shownProducts.forEach((product) => {
        (activityByProduct.get(String(product._id)) || []).slice(0, 20).forEach((activity) => {
          addLink('prd:' + product._id, 'act:' + activity._id, {
            color: '#cbd5e1',
            len: 80,
            spring: 0.03,
          });
        });
      });
      salesShown.slice(0, 200).forEach((sale) => {
        if (!sale.activityId) return;
        addLink('sale:' + sale._id, 'act:' + sale.activityId, {
          color: '#cbd5e1',
          len: 70,
          spring: 0.03,
        });
      });

      // Financeiro: títulos cuja atividade está nessa rede
      if (!hidden.has('financeiro')) {
        const actIds = new Set(actsShown.map((activity) => 'act:' + activity._id));
        const usedBills = bills.filter((bill) => bill.activity && actIds.has('act:' + idOf(bill.activity)));
        usedBills.slice(0, 200).forEach((bill) => {
          addNode({
            id: 'bill:' + bill._id,
            label: money(bill.value),
            type: 'financeiro',
            color: '#8fbf9f',
            size: 7,
            data: bill,
          });
          addLink('bill:' + bill._id, 'act:' + idOf(bill.activity), {
            color: '#8fbf9f',
            len: 70,
            spring: 0.03,
            kind: 'encadeamento',
          });
          const clientId = idOf(bill.client);
          if (clientId && ids.has('cli:' + clientId)) {
            addLink('bill:' + bill._id, 'cli:' + clientId, { color: '#8fbf9f', len: 75, spring: 0.03 });
          }
        });
      }
    }

    return { nodes, links: links.filter((l) => ids.has(l.source) && ids.has(l.target)) };
  }, [activeProducts, activities, sales, bills, people, level, hidden]);

  const visibleGraph = useMemo(() => {
    if (!hidden.size) return graph;
    const nodes = graph.nodes.filter((node) => !hidden.has(node.type));
    const ids = new Set(nodes.map((node) => node.id));
    return { nodes, links: graph.links.filter((l) => ids.has(l.source) && ids.has(l.target)) };
  }, [graph, hidden]);

  const selected = useMemo(
    () => graph.nodes.find((node) => node.id === selectedId) || null,
    [graph, selectedId],
  );

  const toggle = (type) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const openActivity = (activityId, boardId) => {
    if (!activityId) return;
    if (boardId) history.push('/board/' + boardId + '?activity=' + encodeURIComponent(activityId));
  };

  const actionFor = (node) => {
    if (!node) return null;
    if (node.type === 'produto' && node.data && node.data._id) {
      return (
        <Action type="button" onClick={() => history.push('/dashboard/product/' + node.data._id)}>
          Abrir produto <ArrowRightOutlined />
        </Action>
      );
    }
    if (node.type === 'venda' && node.data && node.data._id) {
      return (
        <Action type="button" onClick={() => history.push('/dashboard/sales/' + node.data._id)}>
          Abrir venda <ArrowRightOutlined />
        </Action>
      );
    }
    if ((node.type === 'fornecedor' || node.type === 'cliente') && node.data && node.data._id) {
      return (
        <Action type="button" onClick={() => history.push('/dashboard/people/' + node.data._id)}>
          Abrir cadastro <ArrowRightOutlined />
        </Action>
      );
    }
    if (node.type === 'atividade' && node.boardId) {
      return (
        <Action type="button" onClick={() => openActivity(node.data._id, node.boardId)}>
          Abrir atividade <ArrowRightOutlined />
        </Action>
      );
    }
    return null;
  };

  return (
    <>
      <PageTitleBar title="Confluência de Produtos" backTo="/dashboard" />
      <Shell>
        <Title>
          <h1>{PRODUTOS_NETWORK.title}</h1>
          <p>{PRODUTOS_NETWORK.subtitle}</p>
        </Title>

        <Dock>
          <Search>
            <SearchOutlined />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar produto, venda…"
              aria-label="Buscar produto"
            />
            {search ? (
              <button type="button" onClick={() => setSearch('')} aria-label="Limpar busca">
                <CloseOutlined />
              </button>
            ) : null}
          </Search>
        </Dock>

        <Rail>
          <Card>
            <CardTitle>
              <span>Nível</span>
              <span>{level} de {PRODUTOS_NETWORK.levels.length}</span>
            </CardTitle>
            <Levels>
              {PRODUTOS_NETWORK.levels.map((item) => (
                <LevelBtn
                  key={item.level}
                  type="button"
                  $active={item.level <= level ? 1 : 0}
                  $current={item.level === level ? 1 : 0}
                  onClick={() => setLevel(item.level)}
                  title={item.label}
                >
                  {item.level}
                </LevelBtn>
              ))}
            </Levels>
          </Card>

          <Card>
            <CardTitle>Filtros</CardTitle>
            {PRODUTOS_NETWORK.filterTypes.map((type) => (
              <FilterRow
                key={type}
                type="button"
                $color={PRODUTOS_NETWORK.types[type].color}
                $off={hidden.has(type) ? 1 : 0}
                onClick={() => toggle(type)}
              >
                <i />
                {PRODUTOS_NETWORK.types[type].label}
                <b>{counts[type]}</b>
              </FilterRow>
            ))}
            <div style={{ marginTop: 8, fontSize: 10.5, lineHeight: 1.5, color: '#5f737a' }}>
              Só produto com venda confirmada, fornecedor ou atividade. 1 produtos; 2 vendas e pessoas; 3 atividades e financeiro.
            </div>
          </Card>
        </Rail>

        {selected ? (
          <Panel>
            <PanelHead>
              <i style={{ background: selected.color }} />
              <strong title={selected.label}>{selected.label}</strong>
              <button type="button" onClick={() => setSelectedId(null)} aria-label="Fechar">
                <CloseOutlined />
              </button>
            </PanelHead>
            <Meta>
              <span>Tipo</span>
              <b>{PRODUTOS_TYPE_LABELS[selected.type] || 'Item'}</b>
              {selected.type === 'venda' && selected.data && selected.data.amount ? (
                <>
                  <span>Valor</span>
                  <b>{money(selected.data.amount)}</b>
                </>
              ) : null}
              {selected.type === 'financeiro' && selected.data ? (
                <>
                  <span>Valor</span>
                  <b>{money(selected.data.value)}</b>
                </>
              ) : null}
            </Meta>
            {actionFor(selected)}
          </Panel>
        ) : null}

        <ForceGraph
          graph={visibleGraph}
          selectedId={selectedId}
          search={search}
          levelScale={1}
          onSelect={(node) => setSelectedId(node ? node.id : null)}
        />

        {loading ? (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#8ba1a8', fontSize: 12 }}>
            Carregando produtos…
          </div>
        ) : null}
        {!loading && relationsLoading ? (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#8ba1a8', fontSize: 12 }}>
            Carregando movimentação…
          </div>
        ) : null}
        {!loading && !relationsLoading && !visibleGraph.nodes.length ? (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#8ba1a8', fontSize: 12, textAlign: 'center', maxWidth: 300 }}>
            {error || 'Nenhum produto com venda, fornecedor ou atividade no seu escopo.'}
          </div>
        ) : null}
      </Shell>
    </>
  );
}
